<template>
  <div 
    v-if="shouldShowPlayer"
    class="global-audio-player" 
    :class="{ 
      'has-audio': audioStore.currentCuento
    }"
    @vue:mounted="() => console.log('🎵 GlobalAudioPlayer - Template montado')"
  >
    <!-- Vista compacta -->
    <div 
      class="player-container md:px-10"
      :class="{ 'compact': isMobile }"
      @click="handlePlayerClick"
    >
      <!-- Imagen del cuento (oculta en móvil) -->
      <div class="cover-container">
        <img 
          v-if="audioStore.currentCuento"
          :src="(audioStore.currentCuento.image || audioStore.currentCuento.cover)?.replace('afiche_', 'small_afiche_')" 
          :alt="getFormattedTitle()"
          class="cover-image"
        />
        <div v-else class="cover-image placeholder">
          <svg class="music-icon" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.794L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4.017-2.794a1 1 0 011.617.794z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>

      <!-- Información del cuento -->
      <div class="info-container">
        <h3 class="title">
          {{ getFormattedTitle() }}
        </h3>
        <p class="author">
          {{ getAuthor() }}
        </p>
      </div>

      <!-- Controles de reproducción -->
      <div class="controls-container">
        <button 
          @click.stop="audioStore.togglePlay()"
          class="play-button"
          :class="{ 'playing': audioStore.isPlaying }"
          :disabled="!audioStore.currentCuento || audioStore.isLoading"
        >
          <svg v-if="audioStore.isLoading" class="play-icon animate-spin" fill="none" stroke="white" stroke-width="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="25.133" stroke-dashoffset="25.133">
              <animate attributeName="stroke-dasharray" dur="1.5s" values="0 25.133;12.566 12.566;0 25.133" repeatCount="indefinite"/>
              <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-12.566;-25.133" repeatCount="indefinite"/>
            </circle>
          </svg>
          <svg v-else-if="!audioStore.isPlaying" class="play-icon" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
          </svg>
          <svg v-else class="pause-icon" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>

      <!-- Barra de progreso -->
      <div class="progress-container" @click.stop>
        <div class="time-display">
          <span class="current-time">{{ audioStore.formattedCurrentTime }}</span>
          <span class="duration">{{ audioStore.formattedDuration }}</span>
        </div>
        <div class="progress-bar" @click.stop="seekToPosition">
          <div class="progress-fill" :style="{ width: audioStore.progress + '%' }"></div>
          <div 
            class="progress-thumb" 
            :style="{ left: audioStore.progress + '%' }"
          ></div>
        </div>
      </div>



      <!-- Botón de cerrar -->
      <button @click.stop="handleCloseAudio" class="close-button" :disabled="!audioStore.currentCuento">
        <svg class="close-icon" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- Elemento de audio oculto -->
    <audio 
      ref="audioElement"
      :src="audioStore.audioUrl"
      preload="metadata"
      @loadedmetadata="onAudioLoaded"
      @timeupdate="onTimeUpdate"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
      @volumechange="onVolumeChange"
    ></audio>

    <!-- Modal a pantalla completa para móvil -->
    <div 
      v-if="showFullscreenModal && isMobile"
      class="fixed inset-0 bg-gradient-to-b from-[#E38400] to-[#d17a00] z-[2000] flex items-center justify-center overflow-hidden"
      :class="{ 'dragging': isDragging }"
      @click="closeFullscreenModal"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div class="w-full h-full flex flex-col items-center justify-center gap-8 p-5 relative" @click.stop>
        <!-- Indicador de deslizar -->
        <div class="absolute top-3 left-1/2 transform -translate-x-1/2 z-10">
          <div class="w-10 h-1 bg-white/50 rounded"></div>
        </div>

        <!-- Header con botón de cerrar -->
        <div class="absolute top-5 right-5 z-10">
          <button @click="closeFullscreenModal" class="w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center transition-all duration-300 backdrop-blur-sm hover:bg-white/30 hover:scale-110">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <!-- Cover del cuento (clickeable) -->
        <div class="flex items-center justify-center relative">
          <img 
            v-if="audioStore.currentCuento"
            :src="(audioStore.currentCuento.image || audioStore.currentCuento.cover)?.replace('afiche_', 'small_afiche_')" 
            :alt="getFormattedTitle()"
            class="w-40 h-30 rounded-lg object-cover shadow-lg transition-transform duration-300 hover:scale-105 cursor-pointer"
            @click="navigateToCuentoDetail"
            title="Ver detalles del cuento"
          />
          <div v-else class="w-40 h-30 rounded-lg bg-white/10 flex items-center justify-center text-white">
            <svg class="w-20 h-20 opacity-80" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.794L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4.017-2.794a1 1 0 011.617.794z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>

        <!-- Información del cuento -->
        <div class="text-center max-w-sm">
          <h2 class="text-2xl font-bold text-white mb-2 leading-tight">
            {{ getFormattedTitle() }}
          </h2>
          <p class="text-base text-white/80">
            {{ getAuthor() }}
          </p>
        </div>

        <!-- Controles de reproducción estilo Spotify -->
        <div class="flex items-center justify-center gap-10">
          <button 
            @click="goAnterior"
            :disabled="navegando || !cuentosAdyacentes?.anterior?.documentId || !(cuentosAdyacentes?.anterior?.audio_v2 || cuentosAdyacentes?.anterior?.audio)"
            class="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:bg-white/20 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed relative"
            :title="cuentosAdyacentes?.anterior?.documentId ? 
              (cuentosAdyacentes?.anterior?.audio_v2 || cuentosAdyacentes?.anterior?.audio ? 'Cuento anterior' : 'Cuento anterior sin audio') : 
              'No hay cuento anterior'"
          >
            <svg v-if="!navegando" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z"/>
            </svg>
            <svg v-else class="w-5 h-5 animate-spin" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
            </svg>
          </button>
          
          <button 
            @click="audioStore.togglePlay()"
            class="w-18 h-18 rounded-full bg-white text-[#E38400] flex items-center justify-center transition-all duration-300 shadow-lg hover:scale-105 hover:shadow-xl"
            :class="{ 'animate-pulse': audioStore.isPlaying }"
            :disabled="!audioStore.currentCuento || audioStore.isLoading"
          >
            <svg v-if="audioStore.isLoading" class="w-7 h-7 animate-spin" fill="none" stroke="white" stroke-width="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" stroke="white" stroke-width="2" fill="none" stroke-dasharray="25.133" stroke-dashoffset="25.133">
                <animate attributeName="stroke-dasharray" dur="1.5s" values="0 25.133;12.566 12.566;0 25.133" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-12.566;-25.133" repeatCount="indefinite"/>
              </circle>
            </svg>
            <svg v-else-if="!audioStore.isPlaying" class="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </button>
          
          <button 
            @click="goSiguiente"
            :disabled="navegando || !cuentosAdyacentes?.siguiente?.documentId || !(cuentosAdyacentes?.siguiente?.audio_v2 || cuentosAdyacentes?.siguiente?.audio)"
            class="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:bg-white/20 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed relative"
            :title="cuentosAdyacentes?.siguiente?.documentId ? 
              (cuentosAdyacentes?.siguiente?.audio_v2 || cuentosAdyacentes?.siguiente?.audio ? 'Cuento siguiente' : 'Cuento siguiente sin audio') : 
              'No hay cuento siguiente'"
          >
            <svg v-if="!navegando" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"/>
            </svg>
            <svg v-else class="w-5 h-5 animate-spin" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <!-- Barra de progreso estilo Spotify -->
        <div class="w-full max-w-sm">
          <div class="relative h-1 bg-white/30 rounded cursor-pointer overflow-hidden mb-2" @click="seekToPositionFullscreen">
            <div class="h-full bg-white rounded transition-all duration-100" :style="{ width: audioStore.progress + '%' }"></div>
            <div 
              class="absolute top-1/2 w-3 h-3 bg-white rounded-full transform -translate-x-1/2 -translate-y-1/2 transition-all duration-100 opacity-0 shadow-md"
              :style="{ left: audioStore.progress + '%' }"
            ></div>
          </div>
          <div class="flex justify-between text-xs text-white/80">
            <span>{{ audioStore.formattedCurrentTime }}</span>
            <span>{{ audioStore.formattedDuration }}</span>
          </div>
        </div>


      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useAudioPlayerStore } from '@/stores/audioPlayer';
import { useRouter } from 'vue-router';
import { getCuentosAnteriorSiguiente } from '@/services/cuentos';

const audioStore = useAudioPlayerStore();
const router = useRouter();
const audioElement = ref(null);
const isMobile = ref(false);
const isPlayerHidden = ref(false);
const showFullscreenModal = ref(false);

// Variables para el gesto de deslizar
const touchStartY = ref(0);
const touchStartTime = ref(0);
const isDragging = ref(false);

// Variables para navegación entre cuentos
const cuentosAdyacentes = ref(null);
const navegando = ref(false);

// Variables para reproducción automática
const autoPlayDirection = computed(() => audioStore.autoPlayDirection);

// Computed para determinar si el reproductor debe mostrarse
const shouldShowPlayer = computed(() => {
  return audioStore.currentCuento && !isPlayerHidden.value;
});

console.log('🎵 GlobalAudioPlayer - Script setup ejecutado');
console.log('🎵 GlobalAudioPlayer - Store obtenido:', audioStore);

const getAuthor = () => {
  const cuento = audioStore.currentCuento;
  if (!cuento) return 'Selecciona un cuento para comenzar';
  
  if (cuento?.alumno && (cuento.alumno.nombre || cuento.alumno.apellido)) {
    const nombre = cuento.alumno.nombre || '';
    const apellido = cuento.alumno.apellido || '';
    const nombreCompleto = `${nombre} ${apellido}`.trim();
    return capitalizeWords(nombreCompleto);
  }
  const autor = cuento?.author || cuento?.autor || 'Autor desconocido';
  return capitalizeWords(autor);
};

// Función para capitalizar la primera letra del título
const capitalizeTitle = (title) => {
  if (!title) return '';
  
  // Si todo el título está en mayúsculas, convertirlo a formato título
  if (title === title.toUpperCase()) {
    return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
  }
  
  // Si la primera letra no está en mayúscula, capitalizarla
  if (title.charAt(0) !== title.charAt(0).toUpperCase()) {
    return title.charAt(0).toUpperCase() + title.slice(1);
  }
  
  // Si ya tiene la primera letra en mayúscula, devolver tal como está
  return title;
};

// Función para capitalizar cada palabra del nombre
const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Función para obtener el título formateado
const getFormattedTitle = () => {
  const cuento = audioStore.currentCuento;
  if (!cuento) return 'Selecciona un cuento';
  
  const title = cuento.title || cuento.titulo;
  return capitalizeTitle(title);
};

// Función para navegar al detalle del cuento
const navigateToCuentoDetail = () => {
  if (audioStore.currentCuento?.documentId) {
    router.push(`/cuento/${audioStore.currentCuento.documentId}`);
    // Cerrar el modal si está abierto
    if (showFullscreenModal.value) {
      closeFullscreenModal();
    }
  }
};

// Función para cargar cuentos adyacentes
const cargarCuentosAdyacentes = async () => {
  if (!audioStore.currentCuento?.documentId) return;
  
  try {
    const data = await getCuentosAnteriorSiguiente(audioStore.currentCuento.documentId);
    cuentosAdyacentes.value = data;
  } catch (error) {
    console.error('❌ Error al cargar cuentos adyacentes:', error);
    cuentosAdyacentes.value = null;
  }
};

// Función para navegar al cuento anterior
const goAnterior = async () => {
  if (navegando.value || !cuentosAdyacentes.value?.anterior?.documentId) return;
  
  try {
    navegando.value = true;
    console.log('🔄 Navegando al cuento anterior:', cuentosAdyacentes.value.anterior.documentId);
    
    // Verificar si el cuento anterior tiene audio
    const cuentoAnterior = cuentosAdyacentes.value.anterior;
    const audioUrl = cuentoAnterior.audio_v2 || cuentoAnterior.audio;
    
    if (!audioUrl) {
      console.warn('🎵 El cuento anterior no tiene audio disponible');
      // Mostrar mensaje al usuario o manejar el caso
      return;
    }
    
    // Reproducir el cuento anterior
    audioStore.playCuento(cuentosAdyacentes.value.anterior);
    
    // NO cerrar el modal - mantenerlo abierto
  } catch (error) {
    console.error('❌ Error al navegar al cuento anterior:', error);
  } finally {
    navegando.value = false;
  }
};

// Función para navegar al cuento siguiente
const goSiguiente = async () => {
  if (navegando.value || !cuentosAdyacentes.value?.siguiente?.documentId) return;
  
  try {
    navegando.value = true;
    console.log('🔄 Navegando al cuento siguiente:', cuentosAdyacentes.value.siguiente.documentId);
    
    // Verificar si el cuento siguiente tiene audio
    const cuentoSiguiente = cuentosAdyacentes.value.siguiente;
    const audioUrl = cuentoSiguiente.audio_v2 || cuentoSiguiente.audio;
    
    if (!audioUrl) {
      console.warn('🎵 El cuento siguiente no tiene audio disponible');
      // Mostrar mensaje al usuario o manejar el caso
      return;
    }
    
    // Reproducir el cuento siguiente
    audioStore.playCuento(cuentosAdyacentes.value.siguiente);
    
    // NO cerrar el modal - mantenerlo abierto
  } catch (error) {
    console.error('❌ Error al navegar al cuento siguiente:', error);
  } finally {
    navegando.value = false;
  }
};

// Función para manejar la reproducción automática
const handleAutoPlay = async (direction = 'next') => {
  console.log('🎵 Ejecutando reproducción automática en dirección:', direction);
  
  try {
    let cuentoToPlay = null;
    
    if (direction === 'next' && cuentosAdyacentes.value?.siguiente?.documentId) {
      const cuentoSiguiente = cuentosAdyacentes.value.siguiente;
      const audioUrl = cuentoSiguiente.audio_v2 || cuentoSiguiente.audio;
      
      if (audioUrl) {
        cuentoToPlay = cuentoSiguiente;
        console.log('🎵 Reproduciendo automáticamente cuento siguiente:', cuentoSiguiente.documentId);
      } else {
        console.warn('🎵 Cuento siguiente no tiene audio, intentando anterior');
        // Si el siguiente no tiene audio, intentar el anterior
        if (cuentosAdyacentes.value?.anterior?.documentId) {
          const cuentoAnterior = cuentosAdyacentes.value.anterior;
          const audioUrlAnterior = cuentoAnterior.audio_v2 || cuentoAnterior.audio;
          if (audioUrlAnterior) {
            cuentoToPlay = cuentoAnterior;
            console.log('🎵 Reproduciendo automáticamente cuento anterior:', cuentoAnterior.documentId);
          }
        }
      }
    } else if (direction === 'previous' && cuentosAdyacentes.value?.anterior?.documentId) {
      const cuentoAnterior = cuentosAdyacentes.value.anterior;
      const audioUrl = cuentoAnterior.audio_v2 || cuentoAnterior.audio;
      
      if (audioUrl) {
        cuentoToPlay = cuentoAnterior;
        console.log('🎵 Reproduciendo automáticamente cuento anterior:', cuentoAnterior.documentId);
      } else {
        console.warn('🎵 Cuento anterior no tiene audio, intentando siguiente');
        // Si el anterior no tiene audio, intentar el siguiente
        if (cuentosAdyacentes.value?.siguiente?.documentId) {
          const cuentoSiguiente = cuentosAdyacentes.value.siguiente;
          const audioUrlSiguiente = cuentoSiguiente.audio_v2 || cuentoSiguiente.audio;
          if (audioUrlSiguiente) {
            cuentoToPlay = cuentoSiguiente;
            console.log('🎵 Reproduciendo automáticamente cuento siguiente:', cuentoSiguiente.documentId);
          }
        }
      }
    }
    
    if (cuentoToPlay) {
      // Reproducir el cuento automáticamente
      audioStore.playCuento(cuentoToPlay);
    } else {
      console.log('🎵 No hay más cuentos con audio disponibles para reproducción automática');
    }
  } catch (error) {
    console.error('❌ Error en reproducción automática:', error);
  }
};

const handlePlayerClick = () => {
  if (isMobile.value && audioStore.currentCuento) {
    showFullscreenModal.value = true;
    // Prevenir scroll del body
    document.body.classList.add('modal-open');
  }
};

const closeFullscreenModal = () => {
  showFullscreenModal.value = false;
  // Restaurar scroll del body
  document.body.classList.remove('modal-open');
};

const closeFullscreenModalWithAnimation = () => {
  const modal = document.querySelector('.fullscreen-modal');
  if (modal) {
    modal.style.animation = 'slideOut 0.3s ease-out forwards';
    setTimeout(() => {
      showFullscreenModal.value = false;
      // Restaurar scroll del body
      document.body.classList.remove('modal-open');
    }, 300);
  } else {
    showFullscreenModal.value = false;
    // Restaurar scroll del body
    document.body.classList.remove('modal-open');
  }
};

// Función para manejar la tecla Escape
const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && showFullscreenModal.value) {
    closeFullscreenModal();
  }
};

// Funciones para manejar el gesto de deslizar
const handleTouchStart = (event) => {
  touchStartY.value = event.touches[0].clientY;
  touchStartTime.value = Date.now();
  isDragging.value = false;
};

const handleTouchMove = (event) => {
  if (!touchStartY.value) return;
  
  const currentY = event.touches[0].clientY;
  const deltaY = currentY - touchStartY.value;
  
  // Solo activar si el deslizamiento es hacia abajo y significativo
  if (deltaY > 50) {
    isDragging.value = true;
    event.preventDefault();
  }
};

const handleTouchEnd = (event) => {
  if (!touchStartY.value || !isDragging.value) return;
  
  const currentY = event.changedTouches[0].clientY;
  const deltaY = currentY - touchStartY.value;
  const deltaTime = Date.now() - touchStartTime.value;
  
  // Cerrar si se deslizó hacia abajo más de 100px o con velocidad alta
  if (deltaY > 100 || (deltaY > 50 && deltaTime < 300)) {
    closeFullscreenModalWithAnimation();
  }
  
  // Resetear variables
  touchStartY.value = 0;
  touchStartTime.value = 0;
  isDragging.value = false;
};

const seekToPosition = (event) => {
  if (!audioStore.currentCuento) return;
  
  const rect = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const percentage = (clickX / rect.width) * 100;
  const newTime = (percentage / 100) * audioStore.duration;
  audioStore.seekTo(newTime);
};

const seekToPositionFullscreen = (event) => {
  if (!audioStore.currentCuento) return;
  
  const rect = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const percentage = (clickX / rect.width) * 100;
  const newTime = (percentage / 100) * audioStore.duration;
  audioStore.seekTo(newTime);
};

const onAudioLoaded = () => {
  audioStore.setAudioElement(audioElement.value);
  
  // Si hay un cuento cargado pero no se está reproduciendo, intentar reproducir
  if (audioStore.currentCuento && !audioStore.isPlaying && audioStore.audioUrl) {
    console.log('🎵 Audio cargado, intentando reproducción automática');
    setTimeout(() => {
      if (audioStore.currentCuento && !audioStore.isPlaying) {
        console.log('🎵 Forzando reproducción después de carga');
        audioStore.play();
      }
    }, 500);
  }
};

const onTimeUpdate = () => {
  // El store ya maneja esto automáticamente
};

const onPlay = () => {
  // El store ya maneja esto automáticamente
};

const onPause = () => {
  // El store ya maneja esto automáticamente
};

const onEnded = () => {
  // El store ya maneja esto automáticamente
};

const onVolumeChange = () => {
  // El store ya maneja esto automáticamente
};

const handleCloseAudio = () => {
  // Ocultar el reproductor completamente
  isPlayerHidden.value = true;
  // Cerrar el audio
  audioStore.closeAudio();
};

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 480;
};

// Watch para mostrar el reproductor cuando se carga un nuevo cuento
watch(() => audioStore.currentCuento, (newCuento) => {
  if (newCuento) {
    // Mostrar el reproductor cuando se carga un cuento
    isPlayerHidden.value = false;
    
    // Cargar cuentos adyacentes
    cargarCuentosAdyacentes();
    
    // Forzar reproducción después de un breve delay
    setTimeout(() => {
      if (audioStore.currentCuento && !audioStore.isPlaying && audioStore.audioUrl) {
        console.log('🎵 Forzando reproducción de nuevo cuento');
        audioStore.play();
      }
    }, 1000);
  } else {
    // Limpiar cuentos adyacentes cuando no hay cuento
    cuentosAdyacentes.value = null;
  }
});



// Watch para manejar la restauración del estado persistido
watch(() => audioStore.audioElement, (newElement) => {
  if (newElement && audioStore.currentCuento && audioStore.audioUrl) {
    console.log('🎵 Elemento de audio configurado, restaurando estado persistido');
    
    // Restaurar la fuente de audio si es necesario
    if (newElement.src !== audioStore.audioUrl) {
      newElement.src = audioStore.audioUrl;
    }
    
    // Restaurar el tiempo de reproducción si existe
    if (audioStore.currentTime > 0) {
      newElement.currentTime = audioStore.currentTime;
    }
  }
}, { immediate: true });

// Watch para configurar el elemento de audio cuando esté disponible
watch(audioElement, (newElement) => {
  if (newElement) {
    console.log('🎵 Elemento de audio disponible, configurando con store');
    audioStore.setAudioElement(newElement);
    
    // Si hay un cuento persistido, restaurar inmediatamente
    if (audioStore.currentCuento && audioStore.audioUrl) {
      console.log('🎵 Restaurando estado persistido desde watcher');
      audioStore.restorePersistedState();
    }
  }
}, { immediate: true });

// Watch para asegurar que el elemento de audio esté configurado cuando el reproductor se muestre
watch(shouldShowPlayer, (shouldShow) => {
  if (shouldShow && audioStore.currentCuento && audioStore.audioUrl) {
    console.log('🎵 Reproductor visible, verificando configuración de audio');
    
    // Si el elemento de audio no está configurado, intentar configurarlo
    if (!audioStore.audioElement && audioElement.value) {
      console.log('🎵 Configurando elemento de audio desde watcher de visibilidad');
      audioStore.setAudioElement(audioElement.value);
    }
    
    // Verificar que todo esté configurado correctamente
    audioStore.ensureAudioElementConfigured();
  }
}, { immediate: true });

// Watch para cerrar el modal si se cierra el cuento
watch(() => audioStore.currentCuento, (newCuento) => {
  if (!newCuento && showFullscreenModal.value) {
    closeFullscreenModal();
  }
});

// Watch para restaurar scroll cuando se cierra el modal
watch(showFullscreenModal, (isOpen) => {
  if (!isOpen) {
    document.body.classList.remove('modal-open');
  }
});

onMounted(() => {
  console.log('🎵 GlobalAudioPlayer - onMounted ejecutado');
  
  // Detectar si es móvil
  checkMobile();
  window.addEventListener('resize', checkMobile);
  
  // Agregar listener para la tecla Escape
  document.addEventListener('keydown', handleEscapeKey);
  
  // Agregar listener para reproducción automática
  window.addEventListener('audioAutoPlay', (event) => {
    const { direction } = event.detail;
    handleAutoPlay(direction);
  });
  
  // Configurar el elemento de audio con el store
  if (audioElement.value) {
    audioStore.setAudioElement(audioElement.value);
    console.log('🎵 GlobalAudioPlayer - Elemento de audio configurado');
    
    // Restaurar el estado persistido si existe
    audioStore.restorePersistedState();
  } else {
    console.log('🎵 GlobalAudioPlayer - Elemento de audio no encontrado');
  }
});

onUnmounted(() => {
  audioStore.setAudioElement(null);
  window.removeEventListener('resize', checkMobile);
  document.removeEventListener('keydown', handleEscapeKey);
  window.removeEventListener('audioAutoPlay', handleAutoPlay);
});
</script>

<style scoped>
.global-audio-player {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #E38400;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 12px 16px;
}

.player-container {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 1200px;
  margin: 0 auto;
}

.cover-container {
  flex-shrink: 0;
}

.cover-image {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  background: #f3f4f6;
}

.cover-image.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  color: #9ca3af;
}

.music-icon {
  width: 24px;
  height: 24px;
}

.info-container {
  flex: 1;
  min-width: 0;
}

.title {
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.author {
  font-size: 12px;
  color: white;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.controls-container {
  flex-shrink: 0;
}

.play-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-button.playing {
  background: transparent;
  color: white;
}

.play-button:disabled {
  background: transparent;
  color: #9ca3af;
  cursor: not-allowed;
}

.play-icon, .pause-icon {
  width: 32px;
  height: 32px;
}

/* Spinner blanco en desktop */
.play-button .animate-spin {
  color: white;
}

.progress-container {
  flex: 1;
  min-width: 200px;
  max-width: 400px;
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: white;
  margin-bottom: 4px;
}

.progress-bar {
  position: relative;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #ffffff;
  border-radius: 2px;
  transition: width 0.1s ease;
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.5);
}

.progress-thumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: #ffffff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: left 0.1s ease;
  opacity: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.progress-bar:hover .progress-thumb {
  opacity: 1;
}

.progress-bar:hover {
  background: rgba(255, 255, 255, 0.5);
}

.progress-bar:hover .progress-fill {
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.7);
}

.close-button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.close-button:hover:not(:disabled) {
  background: white;
  color: #ef4444;
}

.close-button:disabled {
  color: #d1d5db;
  cursor: not-allowed;
}

.close-button:disabled:hover {
  background: white;
  color: #d1d5db;
}

.close-icon {
  width: 16px;
  height: 16px;
}



@media (max-width: 768px) {
  .global-audio-player {
    padding: 8px 12px;
    border-top: none;
  }
  
  .player-container {
    gap: 8px;
  }
  
  .cover-image {
    width: 40px;
    height: 40px;
  }
  
  .play-button {
    width: 36px;
    height: 36px;
  }
  
  .progress-container {
    min-width: 120px;
  }
  
  .title {
    font-size: 13px;
  }
  
  .author {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .global-audio-player {
    padding:24px 14px;
    background: white;
    border-top: none;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  .player-container {
    gap: 12px;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .cover-container {
    display: none;
  }
  
  .info-container {
    flex: 1;
    min-width: 0;
  }
  
  .title {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .author {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .controls-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .play-button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #E38400;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .play-button.playing {
    background: transparent;
    color: #E38400;
  }
  
  .play-button:disabled {
    background: transparent;
    color: #9ca3af;
    cursor: not-allowed;
  }
  
  .play-icon, .pause-icon {
    width: 32px;
    height: 32px;
  }
  
  /* Spinner naranja en mobile */
  .play-button .animate-spin {
    color: #E38400;
  }
  
  .close-button {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .close-button:hover:not(:disabled) {
    background: #f3f4f6;
    color: #ef4444;
    transform: scale(1.1);
  }
  
  .close-button:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
  
  .close-button:disabled:hover {
    background: transparent;
    color: #d1d5db;
    transform: none;
  }
  
  .close-icon {
    width: 16px;
    height: 16px;
  }
  

  
  /* Animación de entrada */
  .global-audio-player {
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  

  
  /* Efecto de ripple solo para el botón de cerrar */
  .close-button {
    position: relative;
    overflow: hidden;
  }
  
  .close-button::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.2);
    transform: translate(-50%, -50%);
    transition: width 0.3s, height 0.3s;
  }
  
  .close-button:active::after {
    width: 100px;
    height: 100px;
  }
  
  /* Indicador de estado cuando no hay audio */
  .global-audio-player:not(.has-audio) {
    background: #f9fafb;
  }
  
  /* Mejora de legibilidad para texto largo */
  .info-container {
    position: relative;
  }
  
  .info-container::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 20px;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.8));
    pointer-events: none;
  }
  
  /* Barra de progreso en móvil */
  .progress-container {
    flex: 1;
    min-width: 120px;
    max-width: none;
  }
  
  .time-display {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #6b7280;
    margin-bottom: 4px;
  }
  
  .progress-bar {
    position: relative;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    cursor: pointer;
    overflow: hidden;
  }
  
  .progress-fill {
    height: 100%;
    background: #E38400;
    border-radius: 3px;
    transition: width 0.1s ease;
  }
  
  .progress-thumb {
    position: absolute;
    top: 50%;
    width: 16px;
    height: 16px;
    background: #E38400;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: left 0.1s ease;
    opacity: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .progress-bar:hover .progress-thumb,
  .progress-bar:active .progress-thumb {
    opacity: 1;
  }
  
  /* Mejorar la interactividad de la barra de progreso en móvil */
  .progress-bar {
    touch-action: manipulation;
  }
  
  .progress-bar:active {
    background: #e5e7eb;
  }
  
  .progress-bar:active .progress-fill {
    background: #E38400;
  }
  
  /* Anular efectos de hover de desktop en móvil */
  .progress-bar:hover {
    background: #e5e7eb;
  }
  
  .progress-bar:hover .progress-fill {
    box-shadow: none;
  }
  
  /* Estilos para el modal a pantalla completa */
  .fullscreen-modal {
    animation: fadeIn 0.3s ease-out;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2000;
  }
  
  /* Clase para el efecto de sombra de texto */
  .text-shadow {
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  /* Prevenir scroll cuando el modal está abierto */
  body.modal-open {
    overflow: hidden;
    position: fixed;
    width: 100%;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(100%);
    }
  }
  

  
  /* Animación de pulso para el botón de reproducción */
  @keyframes pulse {
    0% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }
    50% {
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }
    100% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }
  }
  
  /* Efecto de ripple para el botón de reproducción */
  .play-button-spotify {
    position: relative;
    overflow: hidden;
  }
  
  .play-button-spotify::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(29, 185, 84, 0.2);
    transform: translate(-50%, -50%);
    transition: width 0.3s, height 0.3s;
  }
  
  .play-button-spotify:active::after {
    width: 200px;
    height: 200px;
  }
  
  /* Mejoras para el gesto de deslizar */
  .fullscreen-modal {
    touch-action: pan-y;
  }
  
  /* Indicador visual cuando se está deslizando */
  .fullscreen-modal.dragging {
    background: rgba(255, 255, 255, 0.95);
  }
  
  .fullscreen-modal.dragging .modal-content {
    transform: translateY(20px);
    transition: transform 0.2s ease;
  }
}
</style> 